import { useMemo, useState } from "react";
import type { NostrEvent } from "@nostrify/nostrify";
import { nip19 } from "nostr-tools";
import { Code2, Copy, Check } from "lucide-react";
import { Card, CardContent, CardHeader } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { AuthorLine } from "@/components/AuthorLine";
import { NoteContent } from "@/components/NoteContent";
import { getKindInfo, storageClass } from "@/lib/nostrKinds";
import { sanitizeUrl } from "@/lib/sanitizeUrl";

function tagValue(event: NostrEvent, name: string): string | undefined {
  return event.tags.find(([n]) => n === name)?.[1];
}

function eventIdentifier(event: NostrEvent): string {
  const cls = storageClass(event.kind);
  try {
    if (cls === "addressable") {
      const d = tagValue(event, "d") ?? "";
      return nip19.naddrEncode({ kind: event.kind, pubkey: event.pubkey, identifier: d });
    }
    return nip19.neventEncode({ id: event.id, kind: event.kind, author: event.pubkey });
  } catch {
    return event.id;
  }
}

function RawView({ event }: { event: NostrEvent }) {
  const [copied, setCopied] = useState(false);
  const json = useMemo(() => JSON.stringify(event, null, 2), [event]);
  return (
    <div className="relative">
      <Button
        variant="ghost"
        size="icon"
        className="absolute right-1 top-1 h-7 w-7"
        onClick={() => {
          navigator.clipboard?.writeText(json);
          setCopied(true);
          setTimeout(() => setCopied(false), 1500);
        }}
        aria-label="Copy raw event JSON"
      >
        {copied ? <Check className="h-3.5 w-3.5" /> : <Copy className="h-3.5 w-3.5" />}
      </Button>
      <pre className="max-h-80 overflow-auto rounded-lg bg-muted/60 p-3 text-xs leading-relaxed">
        <code>{json}</code>
      </pre>
    </div>
  );
}

function TagTable({ event }: { event: NostrEvent }) {
  if (!event.tags.length) {
    return <p className="text-xs text-muted-foreground">No tags.</p>;
  }
  return (
    <div className="space-y-1">
      {event.tags.map((tag, i) => (
        <div key={i} className="flex gap-2 text-xs font-mono">
          <span className="shrink-0 rounded bg-primary/10 px-1.5 py-0.5 text-primary font-semibold">
            {tag[0]}
          </span>
          <span className="break-all text-muted-foreground">{tag.slice(1).join(" · ")}</span>
        </div>
      ))}
    </div>
  );
}

function ProfileBody({ event }: { event: NostrEvent }) {
  let meta: Record<string, unknown> = {};
  try {
    meta = JSON.parse(event.content);
  } catch {
    return <RawView event={event} />;
  }
  const picture = sanitizeUrl(meta.picture as string | undefined);
  const banner = sanitizeUrl(meta.banner as string | undefined);
  const website = sanitizeUrl(meta.website as string | undefined);
  return (
    <div className="space-y-3">
      {banner && (
        <img src={banner} alt="" className="h-24 w-full rounded-lg object-cover" loading="lazy" />
      )}
      <div className="flex items-center gap-3">
        {picture && (
          <img
            src={picture}
            alt=""
            className="h-14 w-14 rounded-full object-cover ring-2 ring-border"
            loading="lazy"
          />
        )}
        <div className="min-w-0">
          <div className="font-semibold truncate">
            {(meta.display_name as string) || (meta.name as string) || "Unnamed"}
          </div>
          {meta.nip05 ? (
            <div className="text-xs text-muted-foreground truncate">{String(meta.nip05)}</div>
          ) : null}
        </div>
      </div>
      {meta.about ? (
        <p className="text-sm whitespace-pre-wrap text-foreground/90">{String(meta.about)}</p>
      ) : null}
      <div className="flex flex-wrap gap-3 text-xs text-muted-foreground">
        {website && (
          <a href={website} target="_blank" rel="noopener noreferrer" className="hover:underline text-primary">
            {website.replace(/^https?:\/\//, "")}
          </a>
        )}
        {meta.lud16 ? <span>⚡ {String(meta.lud16)}</span> : null}
      </div>
    </div>
  );
}

function AddressableBody({ event }: { event: NostrEvent }) {
  const info = getKindInfo(event.kind);
  const title = tagValue(event, info.render?.titleTag ?? "title");
  const summary = tagValue(event, info.render?.summaryTag ?? "summary");
  const image = sanitizeUrl(tagValue(event, info.render?.imageTag ?? "image"));
  const isMarkdownArticle = event.kind === 30023 || event.kind === 30024;

  return (
    <div className="space-y-3">
      {image && (
        <img src={image} alt="" className="max-h-56 w-full rounded-lg object-cover" loading="lazy" />
      )}
      {title && <h3 className="text-lg font-semibold leading-tight">{title}</h3>}
      {summary && <p className="text-sm text-muted-foreground">{summary}</p>}
      {event.content ? (
        isMarkdownArticle ? (
          <div className="max-h-96 overflow-auto whitespace-pre-wrap rounded-lg bg-muted/40 p-3 text-sm leading-relaxed text-foreground/90">
            {event.content}
          </div>
        ) : (
          <NoteContent event={event} className="text-sm" />
        )
      ) : null}
    </div>
  );
}

export function EventCard({ event }: { event: NostrEvent }) {
  const [showRaw, setShowRaw] = useState(false);
  const info = getKindInfo(event.kind);
  const nip19id = eventIdentifier(event);

  const body = useMemo(() => {
    if (event.kind === 0) return <ProfileBody event={event} />;
    if (storageClass(event.kind) === "addressable" && (info.render?.titleTag || info.render?.imageTag)) {
      return <AddressableBody event={event} />;
    }
    if (info.render?.note && event.content.trim()) {
      return <NoteContent event={event} className="text-sm" />;
    }
    if (event.content.trim() && !info.render?.json) {
      // Generic: show content as preformatted text + tags.
      return (
        <div className="space-y-3">
          <p className="whitespace-pre-wrap break-words text-sm text-foreground/90">
            {event.content}
          </p>
          <TagTable event={event} />
        </div>
      );
    }
    // Tag-only or JSON-content events: show tags prominently.
    return (
      <div className="space-y-3">
        {info.render?.json && event.content ? (
          <pre className="max-h-60 overflow-auto rounded-lg bg-muted/40 p-2 text-xs">
            <code>{(() => {
              try {
                return JSON.stringify(JSON.parse(event.content), null, 2);
              } catch {
                return event.content;
              }
            })()}</code>
          </pre>
        ) : null}
        <TagTable event={event} />
      </div>
    );
  }, [event, info]);

  return (
    <Card className="overflow-hidden border-border/70 bg-card/80 backdrop-blur transition-shadow hover:shadow-md">
      <CardHeader className="gap-2 pb-3">
        <div className="flex items-center justify-between gap-2">
          <AuthorLine pubkey={event.pubkey} timestamp={event.created_at} />
          <Button
            variant="ghost"
            size="icon"
            className="h-7 w-7 shrink-0"
            onClick={() => setShowRaw((v) => !v)}
            aria-label="Toggle raw JSON"
            aria-pressed={showRaw}
          >
            <Code2 className="h-3.5 w-3.5" />
          </Button>
        </div>
        <div className="flex flex-wrap items-center gap-1.5">
          <Badge variant="secondary" className="font-mono text-[10px]">
            kind {event.kind}
          </Badge>
          <Badge variant="outline" className="text-[10px]">
            {info.name}
          </Badge>
          {info.nip ? (
            <Badge variant="outline" className="text-[10px] text-muted-foreground">
              {info.nip}
            </Badge>
          ) : null}
          <Badge variant="outline" className="text-[10px] text-muted-foreground">
            {storageClass(event.kind)}
          </Badge>
        </div>
      </CardHeader>
      <CardContent className="space-y-3">
        {showRaw ? <RawView event={event} /> : body}
        <div className="border-t border-border/60 pt-2">
          <a
            href={`https://njump.me/${nip19id}`}
            target="_blank"
            rel="noopener noreferrer"
            className="font-mono text-[10px] text-muted-foreground hover:text-primary hover:underline break-all"
          >
            {nip19id}
          </a>
        </div>
      </CardContent>
    </Card>
  );
}
